import React, { useState, useEffect } from "react";
import CheckboxGroup from "./CheckboxGroup";

const FOOD_DETAILS = {
  "Meat/Poultry/Fish": [
    "Chicken", "Turkey", "Beef", "Pork", "Fish", "Seafood", "Processed meats",
  ],
  Vegetables: [
    "Carrots",
    "Broccoli",
    "Spinach",
    "Cabbage",
    "Tomatoes",
    "Peppers",
    "Cucumber",
    "Zucchini",
    "Potatoes",
    "Onions",
    "Garlic",
  ],
  Fruits: ["Banana", "Apple", "Berries", "Orange", "Melon", "Grapes", "Pineapple", "Mango", "Peach"],
  Dairy: ["Milk", "Yogurt", "Cheese", "Kefir", "Cottage cheese", "Cream", "Ice cream"],
  "Grains/Bread/Pasta": [
    "Bread",
    "Pasta",
    "Rice",
    "Oatmeal",
    "Quinoa",
    "Couscous",
    "Cereal",
    "Crackers",
  ],
  Eggs: ["Boiled", "Scrambled", "Omelette", "Fried", "Poached"],
  "Legumes (beans, lentils)": ["Beans", "Lentils", "Chickpeas", "Peas", "Soybeans"],
  "Sweets/Desserts": ["Cakes", "Cookies", "Chocolate", "Ice cream", "Pudding", "Candy", "Pastries"],
  Snacks: ["Chips", "Nuts", "Seeds", "Crackers", "Popcorn", "Granola bars", "Dried fruit"],
  Beverages: ["Coffee", "Tea", "Juice", "Milk", "Soda", "Smoothies", "Alcoholic beverages"],
  Other: ["Other"],
};

function getPlaceholder(type, unit) {
  if (type === "weight") return unit === "metric" ? "Enter weight in kg" : "Enter weight in lbs";
  if (type === "height") return unit === "metric" ? "Enter height in cm" : "Enter height";
  return "";
}

// Convert feet & inches to total inches (number)
function feetInchesToInches(feetStr, inchesStr) {
  const feet = parseInt(feetStr, 10) || 0;
  const inches = parseFloat(inchesStr) || 0;
  return feet * 12 + inches;
}

function HeightInput({ unit, height, setHeight, error, onNext, onUnitChange }) {
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");

  // Sync local feet/inches inputs when height or unit changes
  useEffect(() => {
    if (unit === "imperial" && height) {
      const totalInches = parseFloat(height);
      if (!isNaN(totalInches)) {
        setFeet(Math.floor(totalInches / 12).toString());
        setInches((totalInches % 12).toFixed(1));
      } else {
        setFeet("");
        setInches("");
      }
    } else if (unit === "metric") {
      setFeet("");
      setInches("");
    }
  }, [unit, height]);

  // Keep height in parent state updated based on feet/inches combined
  useEffect(() => {
    if (unit === "imperial") {
      const combined = feetInchesToInches(feet, inches);
      setHeight(combined > 0 ? combined.toString() : "");
    }
  }, [feet, inches, unit, setHeight]);

  function handleSubmit(e) {
    e.preventDefault();
    if (unit === "metric") {
      const val = parseFloat(height);
      if (!val || val <= 0) {
        alert("Please enter a valid height in cm.");
        return;
      }
      onNext(val.toString());
    } else {
      const ftNum = parseInt(feet, 10);
      const inNum = parseFloat(inches);
      if (isNaN(ftNum) || ftNum < 0 || ftNum > 10) {
        alert("Feet must be a number between 0 and 10.");
        return;
      }
      if (isNaN(inNum) || inNum < 0 || inNum >= 12) {
        alert("Inches must be between 0 and 11.9.");
        return;
      }
      const totalInches = ftNum * 12 + inNum;
      onNext(totalInches.toString());
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex justify-center mb-4 gap-2">
        <button
          type="button"
          className={`px-4 py-2 rounded-lg border font-medium ${
            unit === "metric"
              ? "bg-[#A3B18A] text-white border-[#A3B18A]"
              : "bg-[#DDE5B6] text-[#4F5D2F] border-[#DDE5B6]"
          }`}
          onClick={() => onUnitChange("metric")}
        >
          Metric
        </button>
        <button
          type="button"
          className={`px-4 py-2 rounded-lg border font-medium ${
            unit === "imperial"
              ? "bg-[#A3B18A] text-white border-[#A3B18A]"
              : "bg-[#DDE5B6] text-[#4F5D2F] border-[#DDE5B6]"
          }`}
          onClick={() => onUnitChange("imperial")}
        >
          Imperial
        </button>
      </div>

      {unit === "metric" ? (
        <input
          type="number"
          min={0}
          max={300}
          step={0.1}
          inputMode="decimal"
          value={height}
          onChange={(e) => setHeight(e.target.value.replace(",", "."))}
          placeholder="Enter height in cm"
          className="w-full p-4 border rounded-lg mb-2 text-lg"
          autoFocus
        />
      ) : (
        <div className="flex gap-2 justify-center">
          <div className="flex flex-col items-center">
            <input
              type="number"
              min={0}
              max={10}
              value={feet}
              onChange={(e) => setFeet(e.target.value)}
              className="w-20 p-4 border rounded-lg mb-1 text-lg"
              placeholder="ft"
            />
            <span className="text-sm font-semibold text-gray-700">ft</span>
          </div>
          <div className="flex flex-col items-center">
            <input
              type="number"
              min={0}
              max={11.9}
              step={0.1}
              value={inches}
              onChange={(e) => setInches(e.target.value)}
              className="w-20 p-4 border rounded-lg mb-1 text-lg"
              placeholder="in"
            />
            <span className="text-sm font-semibold text-gray-700">in</span>
          </div>
        </div>
      )}

      {error && <div className="text-red-600 mb-2">{error}</div>}

      <button
        type="submit"
        className="w-full px-4 py-3 rounded-lg font-semibold bg-[#A3B18A] hover:bg-[#769656] text-white mt-4"
      >
        Next
      </button>
    </form>
  );
}

export default function Question({
  q,
  onNext,
  onUnitChange,
  unit,
  prevValue,
  weight,
  setWeight,
  height,
  setHeight,
  error,
  favoriteFoodGroups,
  setFavoriteFoodDetails,
}) {
  const [textValue, setTextValue] = useState("");
  const [selectedGroups, setSelectedGroups] = useState(prevValue || []);
  const [details, setDetails] = useState({});

  // Food group multi-select
  if (q.type === "food-group-multiselect") {
    function toggleGroup(opt) {
      setSelectedGroups((groups) =>
        groups.includes(opt) ? groups.filter((g) => g !== opt) : [...groups, opt]
      );
    }
    function handleSubmit(e) {
      e.preventDefault();
      onNext(selectedGroups);
    }
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {q.options.map((opt) => (
          <label
            key={opt}
            className="flex items-center gap-4 p-4 rounded-lg border-2 text-lg font-medium bg-[#F6F5EC] border-[#DDE5B6]"
          >
            <input
              type="checkbox"
              checked={selectedGroups.includes(opt)}
              onChange={() => toggleGroup(opt)}
              className="w-7 h-7 rounded-lg accent-[#769656] border-[#A3B18A] mr-2"
              style={{ minWidth: 28, minHeight: 28 }}
            />
            <span>{opt}</span>
          </label>
        ))}
        <button
          type="submit"
          disabled={selectedGroups.length === 0}
          className="w-full px-4 py-3 rounded-lg font-semibold bg-[#A3B18A] hover:bg-[#769656] text-white"
        >
          Next
        </button>
      </form>
    );
  }

  // Food group details
  if (q.type === "food-group-details") {
    const groups = favoriteFoodGroups || [];
    function toggleDetail(group, opt) {
      setDetails((prev) => {
        const prevArr = prev[group] || [];
        let arr;
        if (prevArr.includes(opt)) {
          arr = prevArr.filter((x) => x !== opt);
        } else {
          arr = [...prevArr, opt];
        }
        return { ...prev, [group]: arr };
      });
    }
    function handleOtherChange(group, val) {
      setDetails((prev) => ({ ...prev, [group + "_other"]: val }));
    }
    function handleSubmit(e) {
      e.preventDefault();
      setFavoriteFoodDetails(details);
      onNext(details);
    }
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {groups.map((group) => (
          <div key={group} className="mb-2">
            <div className="font-semibold mb-2 text-[#769656]">{group}:</div>
            <div className="space-y-2">
              {(FOOD_DETAILS[group] || ["Other"]).map((opt) => (
                <div key={opt}>
                  <label className="flex items-center gap-4 p-3 rounded-lg border-2 text-lg font-medium bg-[#F6F5EC] border-[#DDE5B6]">
                    <input
                      type="checkbox"
                      checked={details[group]?.includes(opt) || false}
                      onChange={() => toggleDetail(group, opt)}
                      className="w-6 h-6 rounded-lg accent-[#769656] border-[#A3B18A] mr-2"
                    />
                    <span>{opt === "Other" ? "Please specify" : opt}</span>
                  </label>
                  {opt === "Other" && details[group]?.includes("Other") && (
                    <input
                      type="text"
                      placeholder="Please specify"
                      className="w-full mt-2 p-2 border border-green-400 rounded"
                      onChange={(e) => handleOtherChange(group, e.target.value)}
                      value={details[group + "_other"] || ""}
                      autoFocus
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        <button
          type="submit"
          className="w-full px-4 py-3 rounded-lg font-semibold bg-[#A3B18A] hover:bg-[#769656] text-white"
        >
          Next
        </button>
      </form>
    );
  }

  // Radio input
  if (q.type === "radio") {
    return (
      <div className="space-y-3 mt-2">
        {q.options.map((opt) => (
          <button
            key={opt}
            onClick={() => onNext(opt)}
            className="w-full p-4 rounded-lg border text-lg font-semibold bg-[#DDE5B6] text-[#4F5D2F] border-[#DDE5B6] hover:bg-[#C9DBB2]"
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }

  // Checkbox with 'None' logic
  if (q.type === "checkbox") {
    return <CheckboxGroup key={q.id} options={q.options} onSubmit={onNext} prevValue={prevValue} />;
  }

  // Number input
  if (q.type === "number") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onNext(e.target.elements[0].value);
        }}
      >
        <input
          type="number"
          required
          min={q.min}
          max={q.max}
          className="w-full p-4 border rounded-lg mb-6 text-lg"
          autoFocus
        />
        {error && <div className="text-red-600 mb-2">{error}</div>}
        <button
          type="submit"
          className="w-full px-4 py-3 rounded-lg font-semibold bg-[#A3B18A] hover:bg-[#769656] text-white"
        >
          Next
        </button>
      </form>
    );
  }

  // Weight input
  if (q.type === "weight") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onNext(weight);
        }}
      >
        <div className="flex justify-center mb-4 gap-2">
          <button
            type="button"
            className={`px-4 py-2 rounded-lg border font-medium ${
              unit === "metric"
                ? "bg-[#A3B18A] text-white border-[#A3B18A]"
                : "bg-[#DDE5B6] text-[#4F5D2F] border-[#DDE5B6]"
            }`}
            onClick={() => onUnitChange("metric")}
          >
            Metric
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-lg border font-medium ${
              unit === "imperial"
                ? "bg-[#A3B18A] text-white border-[#A3B18A]"
                : "bg-[#DDE5B6] text-[#4F5D2F] border-[#DDE5B6]"
            }`}
            onClick={() => onUnitChange("imperial")}
          >
            Imperial
          </button>
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={weight}
          onChange={(e) => setWeight(e.target.value.replace(",", "."))}
          className="w-full p-4 border rounded-lg mb-2 text-lg"
          placeholder={getPlaceholder("weight", unit)}
          autoFocus
        />
        {error && <div className="text-red-600 mb-2">{error}</div>}
        <button
          type="submit"
          className="w-full px-4 py-3 rounded-lg font-semibold bg-[#A3B18A] hover:bg-[#769656] text-white"
        >
          Next
        </button>
      </form>
    );
  }

  // Height input (using HeightInput above)
  if (q.type === "height") {
    return (
      <HeightInput
        unit={unit}
        height={height}
        setHeight={setHeight}
        error={error}
        onNext={onNext}
        onUnitChange={onUnitChange}
      />
    );
  }

  // Textarea input
  if (q.type === "textarea") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onNext(textValue);
        }}
        className="space-y-4"
      >
        <textarea
          className="w-full p-4 border border-gray-300 rounded-lg min-h-[120px] text-lg"
          placeholder="Type your response here..."
          autoFocus
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
        />
        <button
          type="submit"
          className="w-full px-4 py-3 rounded-lg font-semibold bg-[#A3B18A] hover:bg-[#769656] text-white"
        >
          Next
        </button>
      </form>
    );
  }

  return null;
}
